<template>
  <b-modal
      :visible="show"
      @hidden="hideEvents"
      :title=this.title
      :id="modalId"
  >

    <vue-element-loading :active="loading" :is-full-screen="false"/>

    <div v-if="confirmMsg">
      <b-row class="text-center" align-v="center">
        <b-col class="p-lg-4">
          <div class="pb-lg-4"><b-img :src="this.icons['warning']" width="40"></b-img></div>

          You are adding <br/><strong>{{ totalActions }}</strong> to the <strong>'{{ value.name }}'</strong> <br/>work plan.<br/>
          The {{(this.selectedCount > 1) ? 'actions' : 'action' }} will be shown with a work plan due date of <br/><strong>{{ value.dueDate }}</strong>.<br/><br/>
          Please confirm you have permission to do this.
        </b-col>
      </b-row>
    </div>
    <div v-else>
      <b-form>
        <b-row>
          <b-col>
            <div class="label pb-1">SELECT A WORK PLAN <span class="text-danger">*</span></div>

            <work-plan-select
              ref="workPlanSelect"
              v-model="value"
              :options="options"
          >
          </work-plan-select>
          </b-col>

          <b-col class="px-2 mb-3">
              <b-row>
                <b-col cols="12">
                  <b-form-group
                      label="OR CREATE NEW PLAN"
                      label-size="sm"
                  >
                    <b-form-input
                        placeholder="Plan name"
                        v-model="newPlanName"
                        ref="newPlanName"
                    ></b-form-input>
                  </b-form-group>
                  <b-form-group
                      label="PLAN DUE DATE"
                      label-size="sm"
                  >
                    <date-picker
                        ref="datePicker"
                        id="internalDueDate"
                        v-model="newPlanDueDate"
                        :confirm-buttons="false"
                        :reduceButtons="false"
                        :calendar-icon="true"
                        :placeholder="'Plan due date'"
                    >
                    </date-picker>
                  </b-form-group>
                  <div class="flex justify-content-between">
                    <b-button
                        :disabled="!this.newPlanName || !this.newPlanDueDate"
                        variant="primary"
                        class="text-center text-nowrap py-1 px-2 filters-toggle"
                        style="width: 220px"
                        @click="save"
                    >
                      CREATE NEW WORK PLAN
                    </b-button>
                  </div>
                </b-col>
              </b-row>
          </b-col>
        </b-row>
      </b-form>
    </div>

    <template slot="modal-footer">
      <b-button
          v-if="confirmMsg"
          variant="outline-secondary"
          @click="goPrev()">
        CANCEL
      </b-button>
      <b-button
          v-else
          variant="outline-secondary"
          @click="hideModal">
        CANCEL
      </b-button>
      <b-button
          v-if="confirmMsg"
          class="btn-arrow"
          variant="primary"
          @click="addActions"
          :disabled="workPlanDisabled"
      >
        CONFIRM
      </b-button>
      <b-button v-else
          class="btn-arrow"
          variant="primary"
          @click="confirmActions"
          :disabled="workPlanDisabled"
      >
        ADD {{ totalActions.toUpperCase() }} TO PLAN
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import ModalMixin from '@/components/Modal/ModalMixin';
import WorkPlanSelect from '@/components/Form/WorkPlanSelect';
import RCard from '@/components/Layout/RCard';
import DatePicker from '@/components/Form/DatePicker';
import WorkPlans from '@/services/WorkPlans/WorkPlans';
import ErrorMixin from '@/components/Notifications/ErrorMixin';

export default {
  name: 'Action-Work-Plan-Modal',
  components: {
    WorkPlanSelect,
    RCard,
    DatePicker,
  },
  props: {
    invalidNodes: {
      default: () => [],
    },

  },
  mixins: [ModalMixin, ErrorMixin],
  data() {
    return {
      modalId: 'actionWorkPlanModal',
      loading: false,
      value: [
        {
          label: '',
          name: '',
          workPlanId: '',
          dueDate: '',
        },
      ],
      options: [],
      confirmMsg: false,
      icons: {
        warning: require('@/assets/images/icons/warning.svg'),
      },
      newPlanDueDate: null,
      newPlanName: null,
      saving: false,
      totalActions: this.selectedCount > 1 ? `${this.selectedCount} actions` : `${this.selectedCount} action`,
      validActions: this.nodes.length > 1 ? `${this.nodes.length} actions were` : `${this.nodes.length} action was`,
      invalidActions: this.invalidNodes.length > 1 ? `${this.invalidNodes.length} actions were` : `${this.invalidNodes.length} action was`,
      title: this.selectedCount > 1 ?
        `Add ${this.selectedCount} actions to a work plan` : `Add ${this.selectedCount} action to a work plan`,
    };
  },
  computed: {
    ...mapGetters({
      clientId: 'userClientId',
    }),
    workPlanDisabled() {
      return this.nodeIds.length === 0 || !this.value || this.loading;
    },
    validNodeIds() {
      return this.nodes.map(node => node.data.clientActionId);
    },
  },
  async mounted() {
    this.getWorkPlans();
  },
  watch: {
  },
  methods: {
    goPrev() {
      this.value = [];
      this.getWorkPlans();
      this.confirmActions();
    },
    getFormatDate(date) {
      const d = new Date(date);
      const day = (`0${d.getDate()}`).slice(-2);
      const month = (`0${d.getMonth() + 1}`).slice(-2);
      return `${day}/${month}/${d.getFullYear()}`;
    },
    clearNewPlan() {
      this.newPlanName = null;
      this.newPlanDueDate = null;
    },
    save() {
      if (!this.newPlanName || !this.newPlanDueDate) {
        this.notifyError('Set a plan name and due date');
        return;
      }
      this.saving = true;
      WorkPlans.create(this.clientId, this.newPlanName, new Date(this.newPlanDueDate).toDateString())
        .then((res) => {
          const newPlan = res.data.data;

          this.value = {
            workPlanId: newPlan.workPlanId,
            label: `${newPlan.name} (${this.getFormatDate(newPlan.dueDate)})`,
            name: newPlan.name,
            dueDate: this.getFormatDate(newPlan.dueDate),
          };

          this.clearNewPlan();
          this.confirmActions();
        })
        .catch((e) => {
          this.showErrorMessage(e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    confirmActions() {
      this.confirmMsg = !this.confirmMsg;
    },
    async addActions() {
      if (this.nodeIds.length === 0 || !this.validate()) {
        return;
      }

      this.loading = true;

      try {
        await this.$store.dispatch('updateWorkPlan', {
          clientActionIds: this.validNodeIds,
          workPlanId: this.value.workPlanId,
        });

        let notifyMsg;
        notifyMsg = `${this.validActions} successfully added to the ${this.value.name} work plan.`;

        if (this.invalidNodes.length > 0) {
          notifyMsg += ` ${this.invalidActions} not added to the plan because they were already approved.`;
        }

        this.$notify({
          title: 'Success',
          text: notifyMsg,
          type: 'success',
        });
        this.$store.dispatch('refreshActions');
      } catch (e) {
        this.$notify({
          title: 'Error',
          text: 'Something went wrong. Please try again later.',
          type: 'error',
        });
      } finally {
        this.hideModal();
      }
    },
    validate() {
      if (this.value.length === 0) {
        this.$notify({
          title: 'Error',
          text: 'Please select or create a work plan',
          type: 'error',
        });
        return false;
      }
      return true;
    },
    async getWorkPlans() {
      this.loading = true;

      try {
        const { data } = await WorkPlans.list(this.clientId);

        this.options = WorkPlans.formatDataToMultiSelectObj(data.data);
      } catch (e) {
        this.$notify({
          title: 'Error',
          text: 'Something went wrong. Please try again later.',
          type: 'error',
        });
      } finally {
        this.loading = false;
      }
    },
    hideModalEvents() {
      this.value = [];
    },
  },
};
</script>

<style lang="scss">
.modal {
  .btn.btn-arrow {
    min-width: 140px;
  }
  .btn-outline-secondary + .btn-arrow {
    margin: auto;
    margin-right: 0;
  }
  .modal-dialog {
    min-width: 900px;
  }
  .modal-content {
    min-height: 500px;
  }
  .col .label, .col-form-label {
    font-size:smaller;
  }
}
</style>
